<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Appraisal</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Special Approvals</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">
          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">Special Approvals</h1>
                <p class="mb-0 small">View all special approvals</p>
              </div>
            </div>

            <div class="form-body">
              <div class="filters-section mb-4" v-if="!loading">
                <div class="row g-3">
                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Year:</label>
                    <select class="form-select form-select-sm" v-model="filters.year">
                      <option value="">All</option>
                      <option v-for="year in uniqueYears" :key="year" :value="year">{{ year }}</option>
                    </select>
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Quarter:</label>
                    <select class="form-select form-select-sm" v-model="filters.quarter">
                      <option value="">All</option>
                      <option value="1">Q1</option>
                      <option value="2">Q2</option>
                      <option value="3">Q3</option>
                      <option value="4">Q4</option>
                    </select>
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Status:</label>
                    <select class="form-select form-select-sm" v-model="filters.status">
                      <option value="">All</option>
                      <option value="Pending">Pending</option>
                      <option value="Approved">Approved</option>
                      <option value="Returned">Returned</option>
                    </select>
                  </div>

                  <div class="col-md-3 d-flex align-items-end">
                    <div class="w-100">
                      <label class="form-label small fw-semibold invisible">Action</label>
                      <button class="btn btn-sm btn-outline-secondary w-100" @click="clearFilters">
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="table-controls mb-3" v-if="!loading">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box" style="max-width: 430px;">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input
                        type="search"
                        v-model="search"
                        class="form-control form-control-sm search-input"
                        placeholder="Search by file number or status..."
                      />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF">Export to PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel">Export to Excel</a>
                  </div>
                </div>
              </div>

              <div v-if="loading" class="loading-state text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading special appraisals...</p>
              </div>

              <div v-else class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>File Number</th>
                        <th>Year</th>
                        <!-- <th>Quarter</th> -->
                        <th class="text-end">Amount Required</th>
                        <th class="text-end">Amount Approved</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="paginatedItems.length === 0">
                        <td colspan="6" class="text-center text-muted">No special appraisals to display.</td>
                      </tr>
                      <tr
                        v-for="(item, index) in paginatedItems"
                        :key="item.id"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'"
                      >
                        <td class="serial-no text-center">{{ (currentPage - 1) * pageLength + index + 1 }}</td>
                        <td class="primary-col">
                          <router-link
                            :to="{ name: 'ViewSpecialApproval', params: { id: item.id } }"
                            class="view-link text-decoration-underline"
                          >
                            {{ item.fileNumber || '-' }}
                          </router-link>
                        </td>
                        <td class="secondary-col">{{ item.budgetYear || '-' }}</td>
                        <!-- <td class="secondary-col">Q{{ item.quarter || '-' }}</td> -->
                        <td class="secondary-col text-end">{{ formatCurrency(item.amtRequired || 0) }}</td>
                        <td class="secondary-col text-end">{{ formatCurrency(item.amtApproved || 0) }}</td>
                        <td class="secondary-col">{{ statusText(item) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="pagination-info" v-if="!loading && filteredItems.length > 0">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Previous</a>
                    </li>
                    <li
                      v-for="page in totalPages"
                      :key="page"
                      class="page-item"
                      :class="{ active: currentPage === page }"
                    >
                      <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import { useUserStore } from '@/stores/user'
import DashboardLayout from '../layout/DashboardLayout.vue'
import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css'

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const userStore = useUserStore()

const specialAppraisals = ref([])
const loading = ref(true)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const debouncedSearch = ref('')
const filters = ref({
  year: '',
  quarter: '',
  status: '',
})

watch(search, debounce((val) => {
  debouncedSearch.value = val
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

watch(filters, () => {
  currentPage.value = 1
}, { deep: true })

const formatCurrency = (amount) => {
  return '₦' + Number(amount || 0).toLocaleString('en-NG')
}

const statusText = (item) => {
  if (Number(item.returnedInd || 0) === 1) return 'Returned'
  if (Number(item.approvedInd || 0) === 1) return 'Approved'
  return 'Pending'
}

const fetchSpecialAppraisals = async () => {
  const token = Cookies.get('auth_token')
  loading.value = true

  try {
    const response = await axios.get(`${root_api_url}/appraisal/special-approval/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []
    specialAppraisals.value = data.map((item) => ({
      id: item.id,
      fileNumber: item.fileNumber,
      budgetYear: item.budgetYear,
      quarter: item.quarter,
      amtRequired: item.amtRequired,
      amtApproved: item.amtApproved,
      returnedInd: item.returnedInd,
      approvedInd: item.approvedInd,
    }))
  } catch (error) {
    if (error?.response?.status === 404) {
      specialAppraisals.value = []
    } else {
      console.error('Failed to fetch special appraisals:', error)
      specialAppraisals.value = []
    }
  } finally {
    loading.value = false
  }
}

const uniqueYears = computed(() => {
  return [...new Set(specialAppraisals.value.map((item) => item.budgetYear).filter(Boolean))].sort((a, b) => Number(b) - Number(a))
})

const clearFilters = () => {
  filters.value = {
    year: '',
    quarter: '',
    status: '',
  }
}

const filteredItems = computed(() => {
  let filtered = specialAppraisals.value

  if (filters.value.year) {
    filtered = filtered.filter((item) => String(item.budgetYear) === String(filters.value.year))
  }

  if (filters.value.quarter) {
    filtered = filtered.filter((item) => String(item.quarter) === String(filters.value.quarter))
  }

  if (filters.value.status) {
    filtered = filtered.filter((item) => statusText(item) === filters.value.status)
  }

  const term = debouncedSearch.value.trim().toLowerCase()
  if (!term) return filtered

  return filtered.filter((item) => {
    return [
      item.fileNumber,
      statusText(item),
      item.budgetYear,
      item.quarter,
    ]
      .join(' ')
      .toLowerCase()
      .includes(term)
  })
})

const exportToPDF = () => {
  const doc = new jsPDF('landscape')

  autoTable(doc, {
    head: [['S/N', 'File Number', 'Year', 'Quarter', 'Amount Required', 'Amount Approved', 'Status']],
    body: filteredItems.value.map((item, index) => [
      index + 1,
      item.fileNumber || '-',
      item.budgetYear || '-',
      item.quarter ? `Q${item.quarter}` : '-',
      formatCurrency(item.amtRequired || 0),
      formatCurrency(item.amtApproved || 0),
      statusText(item),
    ]),
    startY: 18,
    styles: { fontSize: 8 },
  })

  doc.text('Special Appraisals', 14, 12)
  doc.save('special-appraisals.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Special Appraisals')

  worksheet.columns = [
    { header: 'S/N', key: 'sn', width: 8 },
    { header: 'File Number', key: 'fileNumber', width: 22 },
    { header: 'Year', key: 'budgetYear', width: 12 },
    { header: 'Quarter', key: 'quarter', width: 12 },
    { header: 'Amount Required', key: 'amtRequired', width: 18 },
    { header: 'Amount Approved', key: 'amtApproved', width: 18 },
    { header: 'Status', key: 'status', width: 14 },
  ]

  filteredItems.value.forEach((item, index) => {
    worksheet.addRow({
      sn: index + 1,
      fileNumber: item.fileNumber || '-',
      budgetYear: item.budgetYear || '-',
      quarter: item.quarter ? `Q${item.quarter}` : '-',
      amtRequired: Number(item.amtRequired || 0),
      amtApproved: Number(item.amtApproved || 0),
      status: statusText(item),
    })
  })

  worksheet.getRow(1).font = { bold: true }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = 'special-appraisals.xlsx'
  link.click()
  URL.revokeObjectURL(link.href)
}

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(filteredItems.value.length / pageLength.value))
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const changePage = (page) => {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
}

onMounted(async () => {
  document.title = 'View Special Appraisals'

  if (!userStore.profile) {
    await userStore.fetchProfile()
  }

  await fetchSpecialAppraisals()
})
</script>
